<script setup lang="ts">
import { ref, onMounted, onUnmounted, onErrorCaptured } from 'vue';
import { usePageTransitionsStore } from './stores/pageTransitionsStore'
import { useProfileStore } from './stores/profileStore'
import { useRouter } from 'vue-router'
import { PluginListenerHandle } from '@capacitor/core'
import { App as CapacitorApp } from '@capacitor/app'
import { useI18n } from 'vue-i18n'
import { useAppSettingsStore } from './stores/AppSettingsStore';
import PasswordPrompt from './components/PasswordPrompt.vue';
import SnackBar from './components/SnackBar.vue';
import QrLoginConfirmDialog from './components/dialogs/QrLoginConfirmDialog.vue';
import { useSnackbarStore } from './stores/snackbarStore';
import DecoBackground from './components/DecoBackground.vue';
import AppLoader from './components/AppLoader.vue';
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update';
import log from './utils/logger';
const { t } = useI18n()
const router = useRouter()
const profileStore = useProfileStore()
const appSettingsStore = useAppSettingsStore()
const snackbarStore = useSnackbarStore()

const pageTransitionsStore = usePageTransitionsStore()

onErrorCaptured((err, instance, info) => {
  log.debug(`[GO-DEBUG][APP] ✗ onErrorCaptured:`, err)
  log.debug(`[GO-DEBUG][APP] component:`, (instance as any)?.$options?.name || (instance as any)?.$options?.__name || 'unknown')
  log.debug(`[GO-DEBUG][APP] info:`, info)
  // Don't swallow the error — return false to let it propagate
  return false
})

const appListener = ref<PluginListenerHandle | null>(null)

const initialLoading = ref(true)

const passwordError = ref<string | null>(null)

const handleConfirmedPassword = async (password: string) => {
  const success = await profileStore.checkPassword(password).catch((error) => {

    switch (error.message) {
      case 'promptpassword.validate.invalid_password':
        passwordError.value = t(error.message, { attempts: profileStore.passwordCheckAttempts, maxAttempts: profileStore.passwordMaxAttempts })
        break;
      default:
        passwordError.value = t(error.message)
    }

    return false
  })

  if (success) {
    profileStore.showPromptPassword = null
    passwordError.value = null
    profileStore.passwordCheckAttempts = 0
  }
}

const handlePromptClose = () => {
  profileStore.showPromptPassword = null
  passwordError.value = null

  router.back()
}

onMounted(async () => {
  log.debug(`[GO-DEBUG][APP] ▶ onMounted START`)
  log.debug(`[GO-DEBUG][APP] current route: name="${String(router.currentRoute.value.name)}", path="${router.currentRoute.value.path}"`)

  const minDisplay = new Promise(resolve => setTimeout(resolve, 2000))
  log.debug(`[GO-DEBUG][APP] waiting for router.isReady() + 2s min display...`)
  try {
    await Promise.all([router.isReady(), minDisplay])
  } catch {
    // Navigation guard aborted (e.g. OAuth redirect) — stay on loader until location.href navigates away
    return
  }
  log.debug(`[GO-DEBUG][APP] router is ready, hiding loader`)
  initialLoading.value = false

  log.debug(`[GO-DEBUG][APP] post-loader route: name="${String(router.currentRoute.value.name)}", path="${router.currentRoute.value.path}"`)

  // Check if an update is available
  try {
    const updateInfo = await AppUpdate.getAppUpdateInfo();
    // Make sure we are on a mobile platform
    if (appSettingsStore.plateform === 'android' || appSettingsStore.plateform === 'ios') {
      // If an update is available, redirect the user to the update page
      if (updateInfo.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE) {
        log.debug(`[GO-DEBUG][APP] update available → redirecting`)
        appSettingsStore.setUpdateInfo(updateInfo);
        router.push({ name: 'UpdateAvailable' });
        return;
      }
    }
  } catch (error: any) {
    log.debug(`[GO-DEBUG][APP] no update info (expected on web):`, error?.message)
    appSettingsStore.setUpdateInfo(null);
  }

  // await Preferences.clear();
  log.debug(`[GO-DEBUG][APP] loading profile...`)
  await profileStore.load();
  log.debug(`[GO-DEBUG][APP] prompting security type...`)
  await profileStore.promptSecurityType()
  log.debug(`[GO-DEBUG][APP] ◼ onMounted END`)

  appListener.value = await CapacitorApp.addListener('appStateChange', async (state) => {


    await profileStore.load()
    if (state.isActive) {
      // The app is coming back to the foreground from a security prompt
      if (profileStore.prompting) { return }
      await profileStore.promptSecurityType()
    } else {
      profileStore.authenticated = false
    }

    profileStore.prompting = false
  })
})

onUnmounted(async () => {
  await appListener.value?.remove()
})
</script>

<template>

  <div id="app" :class="[appSettingsStore.plateform]" class="min-h-screen relative">
    <DecoBackground />

    <Transition name="fade">
      <AppLoader v-if="initialLoading" />
    </Transition>

    <!-- Bottom safe area cover — hides content behind Android nav bar -->
    <div class="fixed bottom-0 left-0 right-0 z-40 pointer-events-none safe-area-floor" />
    <Transition name="fade" mode="out-in"
      @before-enter="() => log.debug('[GO-DEBUG][TRANSITION] outer fade before-enter')"
      @after-enter="() => log.debug('[GO-DEBUG][TRANSITION] outer fade after-enter')"
      @before-leave="() => log.debug('[GO-DEBUG][TRANSITION] outer fade before-leave')"
      @after-leave="() => log.debug('[GO-DEBUG][TRANSITION] outer fade after-leave')">
      <div>
        <router-view v-slot="{ Component }">
          <Transition :name="pageTransitionsStore.transitionName"
            @before-enter="() => log.debug(`[GO-DEBUG][TRANSITION] page ${pageTransitionsStore.transitionName} before-enter, Component=${(Component as any)?.__name || (Component as any)?.name || 'unknown'}`)"
            @after-enter="() => { log.debug(`[GO-DEBUG][TRANSITION] page after-enter`); pageTransitionsStore.reset() }"
            @before-leave="() => log.debug(`[GO-DEBUG][TRANSITION] page ${pageTransitionsStore.transitionName} before-leave`)"
            @after-leave="() => { log.debug(`[GO-DEBUG][TRANSITION] page after-leave`); pageTransitionsStore.reset() }">
            <component :is="Component" class="min-h-screen" />
          </Transition>
        </router-view>


        <PasswordPrompt :show="profileStore.showPromptPassword === 'validate'"
          :title="$t('promptpassword.validate.title')"
          :description="$t('promptpassword.validate.description')"
          :error="passwordError" @confirmed="handleConfirmedPassword"
          @close="handlePromptClose" />

        <QrLoginConfirmDialog />
      </div>
    </Transition>
    <TransitionGroup name="snack" tag="div"
      class="fixed right-0 left-0 z-50 flex flex-col items-center"
      style="bottom: max(1.25rem, var(--safe-area-bottom, 0px))">
      <SnackBar v-for="snack in snackbarStore.snacks" :key="snack.id"
        class="my-0.5" :color="snack.color"
        @end="snackbarStore.removeSnackbar(snack.id)">
        {{ snack.message }}
      </SnackBar>
    </TransitionGroup>
  </div>
</template>

<style>
.safe-area-floor {
  height: var(--safe-area-bottom, 0px);
  background-color: color-mix(in oklch, var(--background) 60%, transparent);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}
</style>
